import { create } from "zustand";
import { toast } from "react-toastify";
import * as meetingService from "../services/meeting";
import {
  getSiteBoothMembers,
  createSiteBoothMember,
  updateSiteBoothMember,
  deleteSiteBoothMember,
  reinviteSiteBoothMember,
  updateBoothMemberProfile,
  updateSiteExhibitor,
  getSpaceRolePermissions,
  getExhibitorMedia,
  createExhibitorMedia,
  updateExhibitorMedia,
  deleteExhibitorMedia,
  getPromotionalBanners,
  createPromotionalBanner,
  updatePromotionalBanner,
  deletePromotionalBanner,
} from "../services/exhibitorSpace";

export const useExhSpaceStore = create((set, get) => ({
  // Data State
  boothMembers: [],
  leads: [],
  loading: false,
  submitting: false,
  total: 0,
  stats: { total: 0, active: 0, invited: 0 },
  permissions: {},
  permissionsLoading: false,
  materialsLoading: false,
  videos: [],
  collateral: [],
  banners: [],
  bannersLoading: false,
  availability: [],
  bookings: [],
  sentBookings: [],
  meetingsLoading: false,
  memberSettings: null,

  // Pagination & Filter State
  page: 1,
  limit: 10,
  offset: 0,
  searchTerm: "",
  debouncedSearch: "",
  filterStatus: "All",
  filterRole: "All",
  sortBy: "created_at",
  sortOrder: "ASC",
  currentExhibitorId: null,

  // UI State
  isAddSidebarOpen: false,
  isEditSidebarOpen: false,
  selectedMember: null,
  preselectedExhibitorId: null,

  // Booth Materials UI State
  isMaterialSidebarOpen: false,
  materialType: "video", // "video" | "collateral"
  editingMaterial: null,
  isDeleting: false,

  // Setters
  setPage: (page) => set({ page, offset: (page - 1) * get().limit }),
  setLimit: (limit) => set({ limit, page: 1, offset: 0 }),
  setSearchTerm: (searchTerm) => set({ searchTerm }),
  setDebouncedSearch: (debouncedSearch) => set({ debouncedSearch, page: 1, offset: 0 }),
  setFilterStatus: (filterStatus) => set({ filterStatus, page: 1, offset: 0 }),
  setFilterRole: (filterRole) => set({ filterRole, page: 1, offset: 0 }),
  setSortBy: (sortBy) => set({ sortBy }),
  setSortOrder: (sortOrder) => set({ sortOrder }),
  setIsAddSidebarOpen: (isAddSidebarOpen) => set({ isAddSidebarOpen }),
  setIsEditSidebarOpen: (isEditSidebarOpen) => set({ isEditSidebarOpen }),
  setSelectedMember: (setSelectedMember) => set({ selectedMember: setSelectedMember }),
  setPreselectedExhibitorId: (id) => set({ preselectedExhibitorId: id }),
  setIsMaterialSidebarOpen: (isOpen) => set({ isMaterialSidebarOpen: isOpen }),
  setMaterialType: (type) => set({ materialType: type }),
  setEditingMaterial: (material) => set({ editingMaterial: material }),
  setCurrentExhibitorId: (id) => set({ currentExhibitorId: id }),

  // Actions
  fetchBoothMembers: async (eventId, token, exhibitorId) => {
    if (!eventId) return;
    
    // In Exhibitor Space, we always want a specific exhibitorId
    const targetExhibitorId = exhibitorId || get().currentExhibitorId;
    if (!targetExhibitorId) return;

    const {
      limit,
      page,
      debouncedSearch,
      filterStatus,
      filterRole,
      sortBy,
      sortOrder,
    } = get();

    set({ loading: true, currentExhibitorId: targetExhibitorId });
    try {
      const queryParams = {
        event_id: eventId || "",
        exhibitor_id: targetExhibitorId || "",
        limit: limit,
        page: page,
        search: debouncedSearch,
        status: filterStatus !== "All" ? filterStatus : "",
        role_id: filterRole !== "All" ? filterRole : "",
        sortBy: sortBy,
        sortOrder: sortOrder,
        card_dts: true,
      };

      const response = await getSiteBoothMembers(queryParams);

      if (response.data.message === "success" || response.data.success) {
        const resultData = response.data.data.data;
        set({ 
          boothMembers: (resultData && Array.isArray(resultData.data)) ? resultData.data : (Array.isArray(resultData) ? resultData : []), 
          total: (resultData && resultData.total) || 0,
          stats: response.data.data.card_details || { total: 0, active: 0, invited: 0 }
        });
      }
    } catch (error) {
      console.error("Error fetching booth members:", error);
      toast.error("Failed to load booth members");
    } finally {
      set({ loading: false });
    }
  },

  createBoothMember: async (memberData, token, eventId) => {
    set({ submitting: true });
    try {
      const response = await createSiteBoothMember(memberData);

      if (response.data.message === "success" || response.data.success) {
        toast.success(response.data.data?.message || "Booth member added successfully!");
        get().fetchBoothMembers(eventId, token, get().currentExhibitorId);
        set({ isAddSidebarOpen: false });
        return true;
      } else {
        toast.error(response.data.data?.message || response.data.message || response.data.errorMessage || "Failed to add booth member");
        return false;
      }
    } catch (error) {
      console.error("Error creating booth member:", error);
      toast.error(error.response?.data?.message || "Failed to add booth member");
      return false;
    } finally {
      set({ submitting: false });
    }
  },

  updateBoothMember: async (memberId, updateData, token, eventId) => {
    set({ submitting: true });
    try {
      const response = await updateSiteBoothMember(memberId, updateData);

      if (response.data.message === "success" || response.data.success) {
        toast.success(response.data.data?.message || "Booth member updated successfully!");
        get().fetchBoothMembers(eventId, token, get().currentExhibitorId);
        set({ selectedMember: null, isEditSidebarOpen: false });
        return true;
      } else {
        toast.error(response.data.data?.message || response.data.message || response.data.errorMessage || "Failed to update member");
        return false;
      }
    } catch (error) {
      console.error("Error updating booth member:", error);
      toast.error("Failed to update member");
      return false;
    } finally {
      set({ submitting: false });
    }
  },

  deleteBoothMember: async (memberId, token, eventId) => {
    try {
      const response = await deleteSiteBoothMember(memberId);

      if (response.data.message === "success" || response.data.success) {
        toast.success(response.data.data?.message || "Booth member removed successfully!");
        get().fetchBoothMembers(eventId, token, get().currentExhibitorId);
        return true;
      } else {
        toast.error(response.data.data?.message || response.data.message || response.data.errorMessage || "Failed to remove member");
        return false;
      }
    } catch (error) {
      console.error("Error deleting booth member:", error);
      toast.error("Failed to remove member");
      return false;
    }
  },

  reinviteBoothMember: async (memberId) => {
    try {
      const response = await reinviteSiteBoothMember(memberId);
      if (response.data.message === "success" || response.data.success) {
        toast.success(response.data.data?.message || "Invitation & ticket resent successfully!");
        return true;
      } else {
        toast.error(response.data.data?.message || response.data.errorMessage || "Failed to reinvite member");
        return false;
      }
    } catch (error) {
      console.error("Error reinviting booth member:", error);
      toast.error(error.response?.data?.errorMessage || "Failed to reinvite member");
      return false;
    }
  },

  updateProfileSettings: async (memberId, profileData) => {
    set({ submitting: true });
    try {
      const response = await updateBoothMemberProfile(memberId, profileData);

      if (response.data.message === "success" || response.data.success) {
        toast.success(response.data.data?.message || "Profile updated successfully!");
        return true;
      } else {
        toast.error(response.data.data?.message || response.data.message || "Failed to update profile");
        return false;
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.message || "Failed to update profile");
      return false;
    } finally {
      set({ submitting: false });
    }
  },

  updateExhibitor: async (exhibitorId, exhibitorData) => {
    set({ submitting: true });
    try {
      const response = await updateSiteExhibitor(exhibitorId, exhibitorData);

      if (response.data.message === "success" || response.data.success) {
        toast.success(response.data.data?.message || "Exhibitor updated successfully!");
        return true;
      } else {
        toast.error(response.data.data?.message || response.data.message || "Failed to update exhibitor");
        return false;
      }
    } catch (error) {
      console.error("Error updating exhibitor:", error);
      toast.error(error.response?.data?.message || "Failed to update exhibitor");
      return false;
    } finally {
      set({ submitting: false });
    }
  },

  fetchPermissions: async (eventId, roleId) => {
    if (!eventId || !roleId) return;

    set({ permissionsLoading: true });
    try {
      const response = await getSpaceRolePermissions(eventId, roleId);
      

      if (response.data.message === "success" || response.data.status === 200 || response.data.success) {
        const permissionsArray = response.data.data;
        const permissions = {};
        permissionsArray.forEach(p => {
          permissions[p.feature_key] = p;
        });
        set({ permissions });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      // toast.error("Failed to load permissions");
    } finally {
      set({ permissionsLoading: false });
    }
  },

  // Booth Material Actions
  fetchMaterials: async (exhibitorId, token) => {
    if (!exhibitorId) return;
    set({ materialsLoading: true });
    try {
      const response = await getExhibitorMedia(exhibitorId);

      // The backend response Builder sendSuccessfulProcess returns { message: "success", data: { data: [], message: "" } }
      if (response.data.message === "success" || response.data.status === 200 || response.data.success) {
        const resultData = response.data.data;
        const allMedia = resultData?.data || resultData || [];
        
        // The backend returns a mix. We split them for the UI.
        const videos = allMedia.filter(m => m.media_type === "video").map(v => ({
            ...v,
            url: v.file_url,
            // Simple thumbnail logic for YT/Vimeo
            thumbnail: v.file_url?.includes("youtube.com") || v.file_url?.includes("youtu.be") 
                ? `https://img.youtube.com/vi/${v.file_url.split("v=")[1]?.split("&")[0] || v.file_url.split("/").pop()}/maxresdefault.jpg`
                : null
        }));
        const collateral = allMedia.filter(m => m.media_type === "collateral").map(c => ({
            ...c,
            url: c.file_url,
            thumbnail: (c.file_type?.includes("image") || ["jpg", "jpeg", "png"].some(ext => c.file_url?.toLowerCase().endsWith(ext))) ? c.file_url : null
        }));
        set({ videos, collateral });
      }
    } catch (error) {
      console.error("Error fetching materials:", error);
      // toast.error("Failed to load materials");
    } finally {
      set({ materialsLoading: false });
    }
  },

  saveMaterial: async (materialData, token) => {
    const { currentExhibitorId, materialType, editingMaterial } = get();
    
    if (!currentExhibitorId) {
      toast.error("Exhibitor context missing. Please reload the page.");
      return false;
    }

    const isEditing = !!editingMaterial;
    set({ submitting: true });

    try {
      let response;
      const headers = materialType === "collateral" && materialData.file ? { "Content-Type": "multipart/form-data" } : {};

      if (materialType === "collateral" && materialData.file) {
        const formData = new FormData();
        formData.append("exhibitor_id", currentExhibitorId);
        formData.append("media_type", "collateral");
        formData.append("title", materialData.title);
        formData.append("file", materialData.file);
        if (materialData.display_order) formData.append("display_order", materialData.display_order);

        response = isEditing
          ? await updateExhibitorMedia(editingMaterial.id, formData, { headers })
          : await createExhibitorMedia(formData, { headers });
      } else {
        const payload = {
          exhibitor_id: currentExhibitorId,
          media_type: materialType,
          title: materialData.title,
          display_order: materialData.display_order || 0
        };

        if (materialType === "video") {
          const isYoutube = materialData.url?.includes("youtube.com") || materialData.url?.includes("youtu.be");
          const isVimeo = materialData.url?.includes("vimeo.com");
          payload.url = materialData.url;
          payload.file_type = isYoutube ? "youtube" : isVimeo ? "vimeo" : "video";
        }

        response = isEditing
          ? await updateExhibitorMedia(editingMaterial.id, payload, { headers })
          : await createExhibitorMedia(payload, { headers });
        console.log(response.data);
      }

      const success = response.data.message === "success" || response.data.status === 200 || response.data.success;
      if (success) {
        toast.success(`${materialType === "video" ? "Video" : "Collateral"} saved successfully!`);
        get().fetchMaterials(currentExhibitorId, token);
        set({ isMaterialSidebarOpen: false });
        return true;
      }
    } catch (error) {
      console.error("Error saving material:", error);
      toast.error(error.response?.data?.message || "Failed to save material");
    } finally {
      set({ submitting: false });
    }
    return false;
  },

  deleteMaterial: async (id, token) => {
    const { currentExhibitorId } = get();
    set({ isDeleting: true });
    try {
      const response = await deleteExhibitorMedia(id);
      if (response.data.message === "success" || response.data.status === 200 || response.data.success) {
        toast.success("Material removed successfully!");
        get().fetchMaterials(currentExhibitorId, token);
        return true;
      }
    } catch (error) {
      console.error("Error deleting material:", error);
      toast.error("Failed to remove material");
    } finally {
      set({ isDeleting: false });
    }
    return false;
  },

  // Promotional Banner Actions
  fetchBanners: async (exhibitorId, token) => {
    if (!exhibitorId) return;
    set({ bannersLoading: true });
    try {
      const response = await getPromotionalBanners(exhibitorId);

      if (response.data.message === "success" || response.data.status === 200 || response.data.success) {
        set({ banners: response.data.data || [] });
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      set({ bannersLoading: false });
    }
  },

  saveBanner: async (bannerData, token) => {
    const { currentExhibitorId } = get();
    if (!currentExhibitorId) {
      toast.error("Exhibitor context missing");
      return false;
    }

    set({ submitting: true });
    try {
      const isEditing = !!bannerData.id;

      const formData = new FormData();
      formData.append("exhibitor_id", currentExhibitorId);
      formData.append("banner_type", bannerData.banner_type);
      formData.append("title", bannerData.title || "");
      formData.append("description", bannerData.description || "");
      formData.append("button_text", bannerData.button_text || "");
      formData.append("button_link", bannerData.button_link || "");
      formData.append("is_active", 1);
      formData.append("display_order", 0);

      if (bannerData.imageFile) {
        formData.append("image", bannerData.imageFile);
      } else if (bannerData.image_url) {
        formData.append("image_url", bannerData.image_url);
      }

      const response = isEditing
        ? await updatePromotionalBanner(bannerData.id, formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          })
        : await createPromotionalBanner(formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          });

      if (response.data.message === "success" || response.data.status === 200 || response.data.success) {
        toast.success(`Banner ${isEditing ? 'updated' : 'saved'} successfully!`);
        get().fetchBanners(currentExhibitorId, token);
        return true;
      }
    } catch (error) {
      console.error("Error saving banner:", error);
      toast.error(error.response?.data?.message || "Failed to save banner");
    } finally {
      set({ submitting: false });
    }
    return false;
  },

  deleteBanner: async (id, token) => {
    const { currentExhibitorId } = get();
    set({ submitting: true });
    try {
      const response = await deletePromotionalBanner(id);
      if (response.data.message === "success" || response.data.status === 200 || response.data.success) {
        toast.success("Banner removed successfully!");
        get().fetchBanners(currentExhibitorId, token);
        return true;
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      toast.error("Failed to remove banner");
    } finally {
      set({ submitting: false });
    }
    return false;
  },

  // Meeting Actions
  fetchMeetingAvailability: async (exhibitorId) => {
    if (!exhibitorId) return;
    set({ meetingsLoading: true });
    try {
      const response = await meetingService.getExhibitorAvailability(exhibitorId);
      console.log("Exhibitor Availability raw response:", response.data);
      if (response.data.message === "success" || response.data.success) {
        const data = response.data.data?.data || response.data.data || [];
        set({ availability: Array.isArray(data) ? data : [] });
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      set({ meetingsLoading: false });
    }
  },

  fetchMeetingBookings: async (exhibitorId) => {
    if (!exhibitorId) return;
    set({ meetingsLoading: true });
    try {
      const response = await meetingService.getExhibitorBookings(exhibitorId);
      console.log("Bookings raw response:", response.data);
      if (response.data.message === "success" || response.data.success) {
        const data = response.data.data?.data || response.data.data || [];
        set({ bookings: Array.isArray(data) ? data : [] });
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      set({ meetingsLoading: false });
    }
  },

  fetchSentBookings: async (siteUserId) => {
    if (!siteUserId) return;
    set({ meetingsLoading: true });
    try {
      const response = await meetingService.getAttendeeBookings(siteUserId);
      if (response.data.message === "success" || response.data.success) {
        const data = response.data.data?.data || response.data.data || [];
        set({ sentBookings: Array.isArray(data) ? data : [] });
      }
    } catch (error) {
      console.error("Error fetching sent bookings:", error);
    } finally {
      set({ meetingsLoading: false });
    }
  },

  updateBookingStatus: async (bookingId, status, exhibitorId) => {
    set({ submitting: true });
    try {
      const response = await meetingService.updateBooking(bookingId, { status });
      if (response.data.message === "success" || response.data.success) {
        toast.success(`Booking ${status} successfully!`);
        get().fetchMeetingBookings(exhibitorId);
        return true;
      }
    } catch (error) {
      console.error("Error updating booking:", error);
      toast.error("Failed to update booking status");
    } finally {
      set({ submitting: false });
    }
    return false;
  },

  bulkSaveAvailability: async (bulkData, exhibitorId) => {
    set({ submitting: true });
    try {
      const response = await meetingService.bulkSaveAvailability(bulkData);
      if (response.data.success) {
        get().fetchMeetingAvailability(exhibitorId);
        return true;
      }
    } catch (error) {
      console.error("Error bulk saving availability:", error);
      const errorMessage = error.response?.data?.message || "Failed to save availability";
      toast.error(errorMessage);
    } finally {
      set({ submitting: false });
    }
    return false;
  },

  fetchMeetingSettings: async (memberId) => {
    try {
      const response = await meetingService.getMeetingSettings(memberId);
      if (response.data.success) {
        set({ memberSettings: response.data.data.data });
      }
    } catch (error) {
      console.error("Error fetching meeting settings:", error);
    }
  },

  updateMemberSettings: async (settingsData) => {
    set({ submitting: true });
    try {
      const response = await meetingService.updateMeetingSettings(settingsData);
      if (response.data.success) {
        toast.success("Meeting settings updated");
        // Update local state
        set({ memberSettings: { ...get().memberSettings, ...settingsData } });
        return true;
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings");
    } finally {
      set({ submitting: false });
    }
    return false;
  },
}));
